import type { CurrentUser } from './authApi.ts';

/**
 * The last account this device saw, kept beside the tokens that identify it.
 *
 * `/api/auth/me` is the one request the app cannot start without, and it is deliberately
 * the one GET the service worker does not cache: that cache is keyed on URL rather than on
 * a bearer token, so a shared phone would hand the previous person's identity to the next
 * one. Keeping the answer here instead puts it under the same two events that already end
 * a session — signing out, and the server refusing it — rather than under a cache eviction
 * policy that knows nothing about who is signed in.
 *
 * Without it, a reload with no signal had nowhere to get a user id from, so the app either
 * sat on "Loading…" for ever or bounced to the sign-in screen — with a perfectly good
 * session in localStorage and a whole shop's worth of list in the service worker's cache.
 * Which of the two you got depended only on whether `navigator.onLine` had noticed yet.
 */

const CURRENT_USER_KEY = 'bnb.currentUser';

export function readLastKnownUser(): CurrentUser | null {
  try {
    const stored = localStorage.getItem(CURRENT_USER_KEY);

    return stored ? (JSON.parse(stored) as CurrentUser) : null;
  } catch {
    // Private browsing throws on storage access, a truncated value throws on parse. Both
    // mean the same thing here: ask the server, exactly as a first visit does.
    return null;
  }
}

export function rememberUser(user: CurrentUser | null): void {
  try {
    if (user) {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CURRENT_USER_KEY);
    }
  } catch {
    // Non-fatal: the session still works for as long as this tab is open. It just will
    // not survive a reload that happens to land in a dead spot.
  }
}
